/**
 * SIL-1350 - the write half of `/api/client/buyer-verification`, for the 4-step
 * verification modal. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.5.
 *
 * Reads go through `useBuyerStatus()` (one TanStack Query cache entry); these
 * are one-shot writes driven by a button press, so they are plain async
 * functions rather than mutations - there is nothing for a mutation cache to
 * de-duplicate or retry, and the modal already owns the in-flight/error state
 * per step. The single cache entry is refreshed explicitly via
 * `useInvalidateBuyerStatus()` after the writes that change server state the
 * rest of the app can see.
 *
 * Every call goes through `buyerVerificationEndpoint` (hooks/useBuyerStatus.ts)
 * so the endpoint path is still written exactly once in the app.
 *
 * `unwrap` throws `lib/api.ts`'s error (status + parsed body attached), which is
 * what `exchangeErrorMessage` in ./errors reads - no code is translated here.
 */
import {
  BUYER_VERIFICATION_DOC_TYPES,
  BUYER_VERIFICATION_MAX_DOC_BYTES,
  EXCHANGE_UA_VERSION,
  type BuyerVerificationDraft,
  type BuyerVerificationResponse,
} from '@siliconx/shared';
import { unwrap } from '../../lib/api';
import { buyerVerificationEndpoint } from '../../hooks/useBuyerStatus';

/**
 * `PUT /draft`. An EMPTY patch is meaningful and deliberately allowed: the
 * route's `ensureDraft` runs before the update and seeds `legal_name` from
 * `customer_org` and `signatory_name` from the signed-in user, while the
 * `Object.keys(patch).length > 0` guard skips the UPDATE itself. That is how
 * the modal gets the prefilled legal name and signatory the spec calls for on
 * a record that does not exist yet (spec section 4.5, change 2 of 2) without a
 * second endpoint.
 */
export async function saveBuyerVerificationDraft(
  patch: BuyerVerificationDraft,
): Promise<BuyerVerificationResponse> {
  return unwrap(await buyerVerificationEndpoint.draft.$put({ json: patch }));
}

/**
 * A failure of the browser's own POST to S3, as distinct from an API error.
 * S3 answers with an XML body and no `error.code` our table knows, so
 * `exchangeErrorMessage` would fall through to the raw thrown message ("S3
 * upload failed with HTTP 403") and put it on screen. The modal matches on
 * this class and shows the copy module's sentence instead, while the message
 * keeps the status for the console.
 */
export class S3UploadError extends Error {
  constructor(readonly httpStatus: number) {
    super(`S3 upload failed with HTTP ${httpStatus}`);
    this.name = 'S3UploadError';
  }
}

/** Client-side rejection of a file the presigned POST's S3 conditions would refuse anyway. */
export type DocumentRejection = 'type' | 'size';

export function rejectDocument(file: File): DocumentRejection | null {
  if (!(BUYER_VERIFICATION_DOC_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size < 1 || file.size > BUYER_VERIFICATION_MAX_DOC_BYTES) return 'size';
  return null;
}

/**
 * Upload the incorporation document STRAIGHT TO S3 and confirm it.
 *
 * The bytes never touch the API (acceptance criterion): the API hands back a
 * presigned POST, the browser posts the file to S3 itself, and
 * `/document/confirm` then re-heads the staged object server-side and copies it
 * to a final key the client never sees.
 *
 * The spec says "PUT the file"; the endpoint that shipped in slice 1
 * (`presignPost`, apps/api/src/lib/s3.ts) issues a presigned **POST** with
 * `content-length-range` and an exact-`Content-Type` condition, which a PUT
 * cannot carry - so this is a multipart POST. Same property the criterion is
 * about, and S3 enforces the size/type limits on the upload itself rather than
 * trusting anything the browser reports.
 *
 * `fetch` here is the global one, NOT `authFetch`: the presigned form is the
 * only credential S3 accepts, and attaching a Cognito bearer token to a
 * cross-origin S3 request leaks it for no benefit.
 */
export async function uploadBuyerVerificationDocument(
  file: File,
): Promise<BuyerVerificationResponse> {
  const contentType = file.type as (typeof BUYER_VERIFICATION_DOC_TYPES)[number];
  const { url, fields, uploadId } = await unwrap(
    await buyerVerificationEndpoint.document['upload-url'].$post({
      json: { contentType, sizeBytes: file.size, fileName: file.name },
    }),
  );

  const form = new FormData();
  // The presigned fields already include `Content-Type` (it is one of the
  // signed conditions) - appending our own would sign-mismatch. `file` MUST be
  // the last part: S3 ignores any field that follows it.
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  form.append('file', file);

  const res = await fetch(url, { method: 'POST', body: form });
  if (!res.ok) throw new S3UploadError(res.status);

  return unwrap(await buyerVerificationEndpoint.document.confirm.$post({ json: { uploadId } }));
}

/**
 * `POST /sms/send`. `resendAvailableAt` is an ISO timestamp from the server and
 * is the ONLY input to the "Resend in Ns" countdown - a local `setTimeout(30s)`
 * disagrees with the server's own cooldown the moment a tab is backgrounded or
 * the clock drifts, and then offers a resend the API refuses.
 */
export async function sendBuyerVerificationCode(
  phone: string,
): Promise<{ resendAvailableAt: string | undefined }> {
  return unwrap(await buyerVerificationEndpoint.sms.send.$post({ json: { phone } }));
}

/** `POST /sms/verify`. Throws `OTP_MISMATCH` / `OTP_EXPIRED` / `OTP_LOCKED` for ./errors to translate. */
export async function verifyBuyerVerificationCode(code: string): Promise<void> {
  await unwrap(await buyerVerificationEndpoint.sms.verify.$post({ json: { code } }));
}

/**
 * `POST /submit`. The version is the shared literal rather than the response's
 * `exchangeUaVersion` string: `submitSchema` is a `z.literal`, so a drifting
 * client would fail validation at the boundary instead of silently recording an
 * acceptance of the wrong agreement version.
 */
export async function submitBuyerVerification(): Promise<void> {
  await unwrap(
    await buyerVerificationEndpoint.submit.$post({ json: { exchangeUaVersion: EXCHANGE_UA_VERSION } }),
  );
}

/**
 * SIL-1359 — `POST /agreement/accept`. The lightweight re-acceptance for an
 * already-verified org whose accepted agreement version has gone stale: no
 * KYB fields, no document, no phone - just this one call, same literal-version
 * rationale as `submitBuyerVerification` above.
 */
export async function reacceptExchangeUa(): Promise<BuyerVerificationResponse> {
  return unwrap(
    await buyerVerificationEndpoint.agreement.accept.$post({ json: { exchangeUaVersion: EXCHANGE_UA_VERSION } }),
  );
}
